import { motion } from "framer-motion"
import { Button } from "./button"
import { Input } from "./ui/Input"
import { TextArea } from "./ui/TextArea"
import {
    Mail,
    Phone,
    MapPin,
    Send,
    MessageSquare,
    Sparkles,
    Clock,
    TrendingUp,
    Settings,
    Zap,
    LucideIcon,
} from "lucide-react"
import React, { useRef } from 'react'
import emailjs from '@emailjs/browser'
import { toast } from '../utils/alertToast'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

const formSchema = z.object({
    name: z.string().min(1, { message: "Name is required" }),
    email: z.string().email({ message: "Invalid email address" }).min(1, { message: "Email is required" }),
    title: z.string().min(1, { message: "Subject is required" }),
    org: z.string().min(1, { message: "Organization is required" }),
    message: z.string().min(1, { message: "Message is required" }),
})

type FormData = z.infer<typeof formSchema>

type ContactInfoItem = {
    icon: LucideIcon
    title: string
    description: string
    value: string
    gradient: string
    href?: string
}

type FeatureItem = {
    icon: LucideIcon
    title: string
    description: string
    gradient: string
}

const fieldVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: 0.1 + i * 0.06, duration: 0.45 },
    }),
}

export function ContactUs(): JSX.Element {
    const formRef = useRef<HTMLFormElement>(null)
    const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: { name: '', email: '', title: '', org: '', message: '' },
    })

    const sendEmail = async (_data: FormData) => {
        if (!formRef.current) return
        try {
            await emailjs.sendForm(
                'service_gs2va6i',
                'template_mduflxi',
                formRef.current,
                { publicKey: '9fTa8fjyk8uhXehjt' }
            )
            toast.success('Message sent successfully!', { position: 'top-right', autoClose: 3000 })
            reset()
        } catch (error: any) {
            toast.error(`Failed to send message: ${error.text || error.message}`, {
                position: 'top-right',
                autoClose: 5000,
            })
        }
    }

    const contactInfo: ContactInfoItem[] = [
        {
            icon: Mail,
            title: "Email Us",
            description: "Get in touch with our team",
            value: "info@safersavvy.tech",
            gradient: "from-blue-500 to-cyan-500",
            href: "mailto:info@safersavvy.tech",
        },
        {
            icon: Phone,
            title: "Call Us",
            description: "Speak with our experts",
            value: "+92 327 4025364",
            gradient: "from-emerald-500 to-green-600",
            href: "tel:+923274025364",
        },
        {
            icon: MapPin,
            title: "Visit Us",
            description: "Our headquarters",
            value: "UET Lahore — University of Engineering & Technology",
            gradient: "from-fuchsia-500 to-pink-500",
        },
    ]

    const features: FeatureItem[] = [
        {
            icon: Clock,
            title: "24/7 Support",
            description: "Round-the-clock technical support and customer service for all your needs.",
            gradient: "from-[#1D3047] to-[#254874]",
        },
        {
            icon: TrendingUp,
            title: "Proven Results",
            description: "100+ universities have improved transport efficiency by 30%.",
            gradient: "from-emerald-500 to-green-600",
        },
        {
            icon: Settings,
            title: "Custom Solutions",
            description: "Tailored features and integrations for your campus requirements.",
            gradient: "from-fuchsia-500 to-pink-500",
        },
    ]

    return (
        <section
            id="contact"
            className="relative py-20 sm:py-24 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden"
        >
            {/* Ambient background */}
            <div className="pointer-events-none absolute inset-0">
                <motion.div
                    className="absolute -top-32 -right-32 w-96 h-96 bg-[#E3B02E]/10 rounded-full blur-3xl"
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{ duration: 10, repeat: Infinity }}
                />
                <motion.div
                    className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#1D3047]/10 rounded-full blur-3xl"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 12, repeat: Infinity }}
                />
            </div>

            <div className="relative max-w-7xl mx-auto">
                {/* Hero header */}
                <motion.div
                    className="text-center mb-14 sm:mb-16"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E3B02E]/10 border border-[#E3B02E]/20 mb-5">
                        <Sparkles className="w-3.5 h-3.5 text-[#E3B02E]" />
                        <span className="text-[11px] sm:text-xs font-bold tracking-[0.15em] text-[#1D3047]">
                            GET IN TOUCH
                        </span>
                    </span>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-semibold text-[#1D3047] mb-5 leading-tight">
                        Ready to Transform <span className="text-[#E3B02E]">Your Campus?</span>
                    </h2>
                    <p className="max-w-2xl mx-auto text-sm sm:text-base lg:text-lg text-slate-600 leading-relaxed">
                        Join thousands of universities already using SaferSavvy. Let's discuss how we
                        can revolutionize your campus transport with AI-powered innovation.
                    </p>
                </motion.div>

                {/* Info cards row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14 sm:mb-16">
                    {contactInfo.map((info, index) => {
                        const Icon = info.icon
                        const cardInner = (
                            <motion.div
                                className="h-full bg-white rounded-2xl p-6 border border-slate-200/70 shadow-[0_4px_14px_-4px_rgba(29,48,71,0.08)] hover:shadow-[0_12px_28px_-8px_rgba(29,48,71,0.18)] transition-all duration-300 flex flex-col items-center text-center group"
                                whileHover={{ y: -4 }}
                            >
                                <div
                                    className={`w-12 h-12 mb-4 rounded-2xl bg-gradient-to-br ${info.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                                >
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-base font-bold text-[#1D3047] mb-1.5">
                                    {info.title}
                                </h3>
                                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                                    {info.description}
                                </p>
                                <span className="text-sm font-semibold text-[#1D3047] break-all">
                                    {info.value}
                                </span>
                            </motion.div>
                        )
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.08, duration: 0.5 }}
                            >
                                {info.href ? (
                                    <a href={info.href} className="block h-full">
                                        {cardInner}
                                    </a>
                                ) : (
                                    cardInner
                                )}
                            </motion.div>
                        )
                    })}

                    {/* Chat With Us card */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.32, duration: 0.5 }}
                    >
                        <motion.div
                            className="h-full bg-white rounded-2xl p-6 border border-slate-200/70 shadow-[0_4px_14px_-4px_rgba(29,48,71,0.08)] hover:shadow-[0_12px_28px_-8px_rgba(29,48,71,0.18)] transition-all duration-300 flex flex-col items-center text-center group"
                            whileHover={{ y: -4 }}
                        >
                            <div className="w-12 h-12 mb-4 rounded-2xl bg-gradient-to-br from-[#E3B02E] to-amber-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                                <MessageSquare className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-base font-bold text-[#1D3047] mb-1.5">
                                Chat With Us
                            </h3>
                            <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                                Live chat with our support team
                            </p>
                            <Button className="bg-gradient-to-r from-[#E3B02E] to-amber-600 hover:from-amber-500 hover:to-amber-700 text-white text-xs font-semibold px-5 py-2 rounded-lg shadow-md">
                                Start Chat
                            </Button>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Main contact card */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="bg-white rounded-3xl border border-slate-200/70 shadow-[0_24px_60px_-20px_rgba(29,48,71,0.25)] overflow-hidden"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1px_1fr]">
                        {/* Form section */}
                        <div className="p-6 sm:p-8 lg:p-12">
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4 }}
                                className="mb-6 sm:mb-8"
                            >
                                <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-[#1D3047] mb-1.5">
                                    Send us a message
                                </h3>
                                <p className="text-sm text-slate-500">
                                    Fill out the form and we'll get back to you within 24 hours.
                                </p>
                            </motion.div>

                            <form
                                ref={formRef}
                                onSubmit={handleSubmit(sendEmail)}
                                className="space-y-5"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                    <motion.div
                                        custom={0}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true }}
                                        variants={fieldVariants}
                                    >
                                        <label className="block text-xs font-semibold text-[#1D3047] mb-1.5 tracking-wide">
                                            Full Name
                                        </label>
                                        <Input
                                            {...register('name')}
                                            placeholder="Enter your full name"
                                            className="w-full bg-slate-50 border border-slate-200 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-[#E3B02E]/40 focus-visible:border-[#E3B02E] focus-visible:ring-offset-0 rounded-xl px-4 h-12 text-sm transition-colors"
                                        />
                                        {errors.name && (
                                            <p className="text-red-500 text-xs mt-1.5">{errors.name.message}</p>
                                        )}
                                    </motion.div>

                                    <motion.div
                                        custom={1}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true }}
                                        variants={fieldVariants}
                                    >
                                        <label className="block text-xs font-semibold text-[#1D3047] mb-1.5 tracking-wide">
                                            Email Address
                                        </label>
                                        <Input
                                            type="email"
                                            {...register('email')}
                                            placeholder="you@example.com"
                                            className="w-full bg-slate-50 border border-slate-200 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-[#E3B02E]/40 focus-visible:border-[#E3B02E] focus-visible:ring-offset-0 rounded-xl px-4 h-12 text-sm transition-colors"
                                        />
                                        {errors.email && (
                                            <p className="text-red-500 text-xs mt-1.5">{errors.email.message}</p>
                                        )}
                                    </motion.div>
                                </div>

                                <motion.div
                                    custom={2}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    variants={fieldVariants}
                                >
                                    <label className="block text-xs font-semibold text-[#1D3047] mb-1.5 tracking-wide">
                                        Subject
                                    </label>
                                    <Input
                                        {...register('title')}
                                        placeholder="What's this about?"
                                        className="w-full bg-slate-50 border border-slate-200 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-[#E3B02E]/40 focus-visible:border-[#E3B02E] focus-visible:ring-offset-0 rounded-xl px-4 h-12 text-sm transition-colors"
                                    />
                                    {errors.title && (
                                        <p className="text-red-500 text-xs mt-1.5">{errors.title.message}</p>
                                    )}
                                </motion.div>

                                <motion.div
                                    custom={3}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    variants={fieldVariants}
                                >
                                    <label className="block text-xs font-semibold text-[#1D3047] mb-1.5 tracking-wide">
                                        University / Organization
                                    </label>
                                    <Input
                                        {...register('org')}
                                        placeholder="Your university name"
                                        className="w-full bg-slate-50 border border-slate-200 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-[#E3B02E]/40 focus-visible:border-[#E3B02E] focus-visible:ring-offset-0 rounded-xl px-4 h-12 text-sm transition-colors"
                                    />
                                    {errors.org && (
                                        <p className="text-red-500 text-xs mt-1.5">{errors.org.message}</p>
                                    )}
                                </motion.div>

                                <motion.div
                                    custom={4}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    variants={fieldVariants}
                                >
                                    <label className="block text-xs font-semibold text-[#1D3047] mb-1.5 tracking-wide">
                                        Message
                                    </label>
                                    <TextArea
                                        {...register('message')}
                                        placeholder="Tell us about your requirements and how we can help..."
                                        className="w-full bg-slate-50 border border-slate-200 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-[#E3B02E]/40 focus-visible:border-[#E3B02E] focus-visible:ring-offset-0 rounded-xl px-4 py-3 min-h-[140px] text-sm transition-colors resize-none"
                                    />
                                    {errors.message && (
                                        <p className="text-red-500 text-xs mt-1.5">{errors.message.message}</p>
                                    )}
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.5, duration: 0.4 }}
                                    className="flex justify-end pt-2"
                                >
                                    <Button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="group bg-gradient-to-r from-[#1D3047] to-[#254874] hover:from-[#254874] hover:to-[#1D3047] text-white font-semibold py-3 px-7 text-sm rounded-xl shadow-[0_8px_24px_-8px_rgba(29,48,71,0.5)] hover:shadow-[0_12px_28px_-8px_rgba(29,48,71,0.6)] transition-all duration-300 disabled:opacity-70"
                                    >
                                        <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                                        <Send className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </motion.div>
                            </form>
                        </div>

                        {/* Vertical divider */}
                        <div className="hidden lg:block bg-slate-200/70" />

                        {/* Info section */}
                        <div className="p-6 sm:p-8 lg:p-10 bg-gradient-to-b from-slate-50/40 to-white border-t lg:border-t-0 border-slate-200/70">
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.1 }}
                                className="mb-6"
                            >
                                <h3 className="text-2xl font-serif font-semibold text-[#1D3047] mb-1">
                                    Why choose SaferSavvy?
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Trusted by 100+ universities worldwide.
                                </p>
                            </motion.div>

                            <div className="space-y-3 mb-6">
                                {features.map((feature, index) => {
                                    const Icon = feature.icon
                                    return (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: 0.2 + index * 0.08, duration: 0.4 }}
                                            whileHover={{ x: 3 }}
                                            className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-50/70 hover:bg-white hover:shadow-md transition-all duration-300 border border-transparent hover:border-slate-200/70"
                                        >
                                            <div
                                                className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-md`}
                                            >
                                                <Icon className="w-4 h-4 text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-bold text-[#1D3047] mb-1">
                                                    {feature.title}
                                                </h4>
                                                <p className="text-xs text-slate-500 leading-relaxed">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </motion.div>
                                    )
                                })}
                            </div>

                            {/* Response time card */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5, duration: 0.5 }}
                                className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-[#1D3047] to-[#254874] shadow-lg"
                            >
                                <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#E3B02E]/20 rounded-full blur-3xl" />
                                <div className="relative">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-9 h-9 rounded-xl bg-[#E3B02E]/20 border border-[#E3B02E]/30 flex items-center justify-center">
                                            <Zap className="w-4 h-4 text-[#E3B02E]" />
                                        </div>
                                        <h4 className="text-sm font-bold text-white">
                                            Quick Response Time
                                        </h4>
                                    </div>
                                    <p className="text-xs text-slate-300 leading-relaxed">
                                        We typically respond within 2-4 hours during business hours.
                                        For urgent matters, please call us directly.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    )
}

export default ContactUs
